import { NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { CommonModule } from '@angular/common';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AngularMaterialModule } from './angular-material.module';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { CardSoftwareLibraryModule, LOGIN_AUTH_SERVICE, LOGIN_CONFIG, SierraTheme } from '@card-software/library';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { authInterceptor } from './auth.interceptor';
import { siteInterceptor, errorInterceptor, SITE_API_BASE_URL } from '@card-software/nexus-utilities';
import { environment } from '../environments/environment';
import { PrimengModule } from './primeng/primeng.module';
import { FieldsetModule } from 'primeng/fieldset';
import { providePrimeNG } from 'primeng/config';
import { UploadIcon } from 'primeng/icons';
import { ConfirmationService } from 'primeng/api';
import { TranslateLocalizedPipe } from './pipes/translate-localized.pipe';
import { HnDatePipe } from './pipes/hn-date.pipe';
import { CustomTranslateLoader } from './services/custom.translate.loader';
import { TranslateWithFallbackPipe } from './pipes/translate-with-fallback.pipe';
import { UserAuthenticationService } from './services/user-authentication.service';
import { InventoryTransactionComponent } from './components/common/inventory-transaction/inventory-transaction/inventory-transaction.component';
import { InventoryTypeComponent } from './components/inventory-type/inventory-type.component';
import { InventoryTypeModalComponent } from './components/common/modals/inventory-type-modal/inventory-type-modal.component';
import { ItemsInventoryComponent } from './components/items-inventory/items-inventory.component';
import { ItemsInventoryDetailComponent } from './components/items-inventory-detail/items-inventory-detail.component';
import { ItemsInventoryFiltersComponent } from './components/common/items-inventory-filters/items-inventory-filters.component';
import { ItemsInventoryGroupTableComponent } from './components/common/items-inventory-group-table/items-inventory-group-table.component';
import { RollQcDetailComponent } from './components/roll-qc/roll-qc-detail/roll-qc-detail.component';
import { SplitLotItemsListComponent } from './components/split-lot-items/split-lot-items-list/split-lot-items-list.component';
import { SplitLotItemsDetailComponent } from './components/split-lot-items/split-lot-items-detail/split-lot-items-detail.component';
import { MergeLotItemsListComponent } from './components/merge-lot-items/merge-lot-items-list/merge-lot-items-list.component';
import { MergeLotItemsDetailComponent } from './components/merge-lot-items/merge-lot-items-detail/merge-lot-items-detail.component';
import { TestBarcodeComponent } from './components/yarn-inventory/test-barcode/test-barcode.component';
import { TransferUnitsComponent } from './components/transfer-units/transfer-units/transfer-units.component';
import { TransferUnitDetailsComponent } from './components/transfer-units/transfer-unit-details/transfer-unit-details.component';
import { WarehouseImportsComponent } from './components/warehouse-inventory/warehouse-imports/warehouse-imports.component';
import { WarehouseImportsDetailComponent } from './components/warehouse-inventory/warehouse-imports-details/warehouse-imports-details.component';
import { YarnRequisitionsComponent } from './components/yarn-requisitions/yarn-requisitions/yarn-requisitions.component';
import { YarnRequisitionsDetailsComponent } from './components/yarn-requisitions/yarn-requisitions-details/yarn-requisitions-details.component';
import { YarnsGroupDetailsPanelComponent } from './components/common/yarns-group-details-panel/yarns-group-details-panel.component';
import { YarnsQcComponent } from './components/yarn-qc/yarns-qc/yarns-qc.component';
import { YarnQcDetailsComponent } from './components/yarn-qc/yarn-qc-details/yarn-qc-details.component';
import { RollDetailsComponent } from './components/common/modals/roll-details/roll-details.component';
import { QrScannerComponent } from './shared/components/qr-scanner/qr-scanner.component';
import { QrScannerModalComponent } from './components/common/qr-scanner-modal/qr-scanner-modal.component';
import { SupplyRequestListComponent } from './components/supply-request/supply-request-list/supply-request-list.component';
import { SupplyRequestDetailComponent } from './components/supply-request/supply-request-detail/supply-request-detail.component';
import { RequisitionFormModalComponent } from './components/common/modals/requisition-form-modal/requisition-form-modal.component';
import { WeightControlComponent } from './components/common/weight-control/weight-control.component';
import { UnauthorizedComponent } from './components/unauthorized/unauthorized.component';
import { ReturnSurplusComponent } from './components/return-surplus/return-surplus.component';
import { AdjustWeightComponent } from './components/common/modals/adjust-weight/adjust-weight.component';
import { SupplyWarehouseListComponent } from './components/supply-warehouse/supply-warehouse-list/supply-warehouse-list.component';
import { SupplyWarehouseDetailComponent } from './components/supply-warehouse/supply-warehouse-detail/supply-warehouse-detail.component';
import { InternalReturnTabComponent } from './components/supply-warehouse/supply-warehouse-list/tabs/internal-return-tab/internal-return-tab.component';
import { CompletedDispatchTabComponent } from './components/supply-warehouse/supply-warehouse-list/tabs/completed-dispatch-tab/completed-dispatch-tab.component';
import { ProviderReturnTabComponent } from './components/supply-warehouse/supply-warehouse-list/tabs/provider-return-tab/provider-return-tab.component';
import { ReportingComponent } from './components/reporting/reporting.component';
import { InventoryAdjustmentComponent } from './components/inventory-adjustment/inventory-adjustment.component';
import { InventoryAdjustmentDetailsComponent } from './components/inventory-adjustment/inventory-adjustment-details.component';
import { LocationsComponent } from './components/locations/locations.component';
import { LocationModalComponent } from './components/common/modals/location-modal/location-modal.component';
import { DispatchInstructionDetailsComponent } from './components/dispatch-instructions/dispatch-instruction-details/dispatch-instruction-details.component';
import { DispatchInstructionsComponent } from './components/dispatch-instructions/dispatch-instructions/dispatch-instructions.component';
import { FinishedGoodTransfersComponent } from './components/production-card/finished-good-transfers/finished-good-transfers.component';
import { FinishedGoodTransferDetailsComponent } from './components/production-card/finished-good-transfer-details/finished-good-transfer-details.component';
import { LotItemTransfersComponent } from './components/lot-item-transfers/lot-item-transfers/lot-item-transfers.component';
import { LotItemTransferDetailsComponent } from './components/lot-item-transfers/lot-item-transfer-details/lot-item-transfer-details.component';
import { LotItemRelocationDetailsComponent } from './components/lot-item-relocation/lot-item-relocation-details/lot-item-relocation-details.component';
import { LotItemRelocationComponent } from './components/lot-item-relocation/lot-item-relocation/lot-item-relocation.component';
import { ProductionInventoryComponent } from './components/production-inventory/production-inventory.component';
import { ProductionInventoryFiltersComponent } from './components/common/production-inventory-filters/production-inventory-filters.component';
import { SecondQualityDispatchListComponent } from './components/second-quality-dispatches/second-quality-dispatch-list/second-quality-dispatch-list.component';
import { SecondQualityDispatchDetailsComponent } from './components/second-quality-dispatches/second-quality-dispatch-details/second-quality-dispatch-details.component';
import { DispatchRollsTreeTableComponent } from './components/common/tables/dispatch-rolls-tree-table/dispatch-rolls-tree-table.component';
import { RemissionGuidesComponent } from './components/remission-guides/remission-guides/remission-guides.component';
import { RemissionGuideDetailsComponent } from './components/remission-guides/remission-guide-details/remission-guide-details.component';
import { InventorySessionsComponent } from './components/inventory-sessions/inventory-sessions/inventory-sessions.component';
import { InventorySessionDetailsComponent } from './components/inventory-sessions/inventory-session-details/inventory-session-details.component';
import { CreateChildSessionDialogComponent } from './components/inventory-sessions/create-child-session-dialog/create-child-session-dialog.component';
import { BalanceSnapshotRunsComponent } from './components/balance-snapshot-runs/balance-snapshot-runs.component';
import { KardexAuditDashboardComponent } from './components/kardex-audit/kardex-audit-dashboard/kardex-audit-dashboard.component';
import { KardexRollHistoryDialogComponent } from './components/kardex-audit/kardex-roll-history-dialog/kardex-roll-history-dialog.component';
import { BusinessLotNumbersComponent } from './components/business-lot-numbers/business-lot-numbers.component';
import { BusinessLotNumbersModalComponent } from './components/business-lot-numbers/business-lot-numbers-modal.component';
import { RequisitionsComponent } from './components/requisition-details/requisitions/requisitions.component';
import { RequisitionDetailComponent } from './components/requisition-details/requisition-detail/requisition-detail.component';

@NgModule({
  declarations: [
    AppComponent,

    InventoryTransactionComponent,
    InventoryTypeComponent,
    InventoryTypeModalComponent,
    ItemsInventoryComponent,
    ItemsInventoryDetailComponent,
    ItemsInventoryFiltersComponent,
    ItemsInventoryGroupTableComponent,
    RollDetailsComponent,
    RollQcDetailComponent,
    SplitLotItemsListComponent,
    SplitLotItemsDetailComponent,
    MergeLotItemsListComponent,
    MergeLotItemsDetailComponent,
    TestBarcodeComponent,
    TransferUnitsComponent,
    TransferUnitDetailsComponent,
    WarehouseImportsComponent,
    WarehouseImportsDetailComponent,
    YarnRequisitionsComponent,
    YarnRequisitionsDetailsComponent,
    YarnsGroupDetailsPanelComponent,
    YarnsQcComponent,
    YarnQcDetailsComponent,
    SupplyRequestListComponent,
    SupplyRequestDetailComponent,
    RequisitionFormModalComponent,
    WeightControlComponent,
    QrScannerComponent,
    QrScannerComponent,
    QrScannerModalComponent,
    SupplyRequestListComponent,
    SupplyRequestDetailComponent,
    RequisitionFormModalComponent,
    UnauthorizedComponent,
    ReturnSurplusComponent,
    AdjustWeightComponent,
    InventoryAdjustmentComponent,
    InventoryAdjustmentDetailsComponent,
    ReportingComponent,
    LocationsComponent,
    LocationModalComponent,
    SupplyWarehouseListComponent,
    SupplyWarehouseDetailComponent,
    InternalReturnTabComponent,
    CompletedDispatchTabComponent,
    ProviderReturnTabComponent,
    ReportingComponent,
    DispatchInstructionDetailsComponent,
    DispatchInstructionsComponent,
    FinishedGoodTransfersComponent,
    FinishedGoodTransferDetailsComponent,
    LotItemTransfersComponent,
    LotItemTransferDetailsComponent,
    LotItemRelocationDetailsComponent,
    LotItemRelocationComponent,
    ProductionInventoryComponent,
    ProductionInventoryFiltersComponent,
    SecondQualityDispatchListComponent,
    SecondQualityDispatchDetailsComponent,
    DispatchRollsTreeTableComponent,
    RemissionGuidesComponent,
    RemissionGuideDetailsComponent,
    InventorySessionsComponent,
    InventorySessionDetailsComponent,
    CreateChildSessionDialogComponent,
    KardexAuditDashboardComponent,
    BalanceSnapshotRunsComponent,
    KardexRollHistoryDialogComponent,
    BusinessLotNumbersComponent,
    BusinessLotNumbersModalComponent,
    RequisitionsComponent,
    RequisitionDetailComponent,
  ],
  imports: [
    AppRoutingModule,
    AngularMaterialModule,
    BrowserModule,
    BrowserAnimationsModule,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    CardSoftwareLibraryModule,
    PrimengModule,
    FieldsetModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useClass: CustomTranslateLoader,
      },
    }),
    TranslateWithFallbackPipe,
    TranslateLocalizedPipe,
    HnDatePipe,
    UploadIcon,
  ],
  exports: [TranslateWithFallbackPipe, TranslateLocalizedPipe, HnDatePipe],
  providers: [
    { provide: LOGIN_AUTH_SERVICE, useClass: UserAuthenticationService },
    {
      provide: LOGIN_CONFIG,
      useValue: {
        domainAppName: 'Sierra Inventory',
        contactEmail: 'it-support-sierra-textiles@card-software.com',
        redirectUrl: '/warehouse-imports',
      },
    },
    provideHttpClient(withInterceptors([authInterceptor, siteInterceptor, errorInterceptor])),
    providePrimeNG({
      theme: {
        preset: SierraTheme,
        options: {
          darkModeSelector: false,
        },
      },
      translation: {
        emptyFilterMessage: 'No se encontraron resultados',
        emptyMessage: 'No hay datos disponibles',
      },
    }),
    ConfirmationService,
    { provide: SITE_API_BASE_URL, useValue: environment.proxyUrls.userMasterApi },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
